import { triggerSegmentAction } from '@/src/services/segment'
import type { SegmentAction, SegmentComponent } from '@/src/services/segment/triggerSegmentEvents'
import { type ComponentProps, type PropsWithChildren } from 'react'
import { isE2E } from '@/src/common/environment'
import { Text, type TextStyle, StyleSheet, View } from 'react-native'
import { Spinner, Button as TamaguiButton } from 'tamagui'
import { fontFamilies, mesaColorNames } from '@/src/common/theme'
import * as Branding from '@/src/experiences/branding'

type LegacySize = 'sm' | 'md' | 'lg'
const defaultLegacySize = 'md' satisfies LegacySize

interface ButtonProps
  extends Pick<ComponentProps<'button'>, 'title'>,
    Omit<ComponentProps<typeof TamaguiButton>, 'size' | 'variant'> {
  loading?: boolean
  segmentAction?: SegmentAction | SegmentAction[]
  segmentComponent?: SegmentComponent
  /** @deprecated are you sure you don't want to just use {@link ButtonProps.title} or {@link ButtonProps.loading} instead? */
  children?: PropsWithChildren['children']
  /** @deprecated */
  buttonStyle?: ComponentProps<typeof View>['style']
  /** @deprecated */
  containerStyle?: ComponentProps<typeof View>['style']
  /** @deprecated */
  titleStyle?: ComponentProps<typeof Text>['style']
  variant?: ComponentProps<typeof TamaguiButton>['variant'] | 'text'
  // King: Extend ComponentProps<typeof TamaguiButton>['size'] post pairity
  size?: LegacySize
}

export const buttonMaxWidth = 320

export const Button = ({
  segmentAction,
  segmentComponent = 'Button',
  onPress,
  size = defaultLegacySize,
  variant,
  ...props
}: ButtonProps) => {
  const height = getHeightFromButtonSize(size)
  const loading = !isE2E && props.loading
  const { colorValue: primaryColor } = Branding.Hooks.usePrimaryColor()

  const titleStyle = {
    color: props.disabled
      ? mesaColorNames['Greyscale/500 #6E7375']
      : variant === 'text'
        ? primaryColor
        : mesaColorNames['Greyscale/900 #152438'],
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 16,
    textDecorationLine: 'none',
  } as const satisfies TextStyle

  return (
    <TamaguiButton
      animation="200ms"
      circular
      pressStyle={{ opacity: 0.3 }}
      {...(variant === 'text' && { borderWidth: 0 })}
      {...props}
      variant={variant === 'text' ? undefined : variant}
      chromeless={variant === 'text' ? true : props.chromeless}
      style={StyleSheet.flatten([
        {
          paddingTop: 10,
          paddingBottom: 10,
          minHeight: height,
          alignItems: 'center',
          margin: 'auto',
          width: buttonMaxWidth,
          maxWidth: '100%',
        },
        variant === 'text'
          ? { backgroundColor: 'transparent' }
          : variant === 'outlined'
            ? undefined
            : {
                backgroundColor: props.disabled
                  ? mesaColorNames['semantic/bg/disabled #DADDDE']
                  : primaryColor,
              },
        titleStyle,
        StyleSheet.flatten([props.style]),
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        props.containerStyle,
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        props.buttonStyle,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      ])}
      onPress={(...args) => {
        onPress?.(...args)

        if (!segmentAction) return

        if (Array.isArray(segmentAction)) {
          return segmentAction.forEach((singleSegmentAction) => {
            void triggerSegmentAction({
              component: segmentComponent,
              action: singleSegmentAction,
            })
          })
        }

        void triggerSegmentAction({
          component: segmentComponent,
          action: segmentAction,
        })
      }}
    >
      {loading ? (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- nondeterministic type complexity
        // @ts-ignore union too complex from Tamagui
        <Spinner color={titleStyle.color} />
      ) : (
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        props.children || (
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          <Text style={StyleSheet.flatten([titleStyle, props.titleStyle])}>{props.title}</Text>
        )
      )}
    </TamaguiButton>
  )
}

function getHeightFromButtonSize(size: LegacySize) {
  switch (size) {
    case 'md':
      return 48
    case 'sm':
      return 40
    case 'lg':
      return 60
  }
}
